'use client';

import React from 'react';
import { useRouter } from 'next/router';
import { ExperienceType } from '../types';
import ShareButton from '@/components/common/ShareButton';
import CurrencyDisplay from '@/components/common/CurrencyDisplay';
import { BackendCurrency } from '@/utils/currencyHelpers';
import { typography } from '@/design-system/tokens/typography';

interface ExperienceHeaderProps {
  experience: unknown;
  type: ExperienceType;
  slug?: string;
  /** Live traveler count from the booking form; when set, stay pricing reflects the matching group tier or standard rate */
  travelerCount?: number;
  /** Share the current page URL instead of the canonical experience route */
  shareCurrentUrl?: boolean;
}

export const ExperienceHeader: React.FC<ExperienceHeaderProps> = ({
  experience,
  type,
  slug,
  travelerCount,
  shareCurrentUrl = false,
}) => {
  const router = useRouter();
  const hasBookingIdInUrl = Boolean(router.query?.booking);

  if (!experience) return null;

  const getTitle = () => {
    const exp = experience as {
      shortTitle?: string | null;
      title?: string;
      name?: string;
    };

    // Use the same priority as list cards and detail page: shortTitle > title > name
    const isNonEmpty = (str: string | null | undefined): boolean => {
      return (
        str !== null &&
        str !== undefined &&
        typeof str === 'string' &&
        str.trim().length > 0
      );
    };

    if (isNonEmpty(exp.shortTitle)) {
      return exp.shortTitle!.trim();
    }
    if (isNonEmpty(exp.title)) {
      return exp.title!.trim();
    }

    if (isNonEmpty(exp.name)) {
      return exp.name.trim();
    }

    return '';
  };

  const getCategories = (): string[] => {
    const exp = experience as {
      category?:
        | string
        | { title?: string; name?: string; [key: string]: unknown }
        | Array<{ title?: string; name?: string; [key: string]: unknown }>;
      categories?: Array<
        string | { title?: string; name?: string; [key: string]: unknown }
      >;
      type?: string;
    };
    const categories: string[] = [];

    console.log('[ExperienceHeader] getCategories - Raw data:', {
      category: exp?.category,
      categories: exp?.categories,
      categoryType: typeof exp?.category,
      isCategoryArray: Array.isArray(exp?.category),
      categoriesLength: Array.isArray(exp?.categories)
        ? exp.categories.length
        : 0,
    });

    // Helper function to extract category name from various formats
    const extractCategoryName = (cat: unknown): string | null => {
      if (!cat) return null;
      if (typeof cat === 'string') {
        const trimmed = cat.trim();
        return trimmed.length > 0 ? trimmed : null;
      }
      if (typeof cat === 'object' && cat !== null && !Array.isArray(cat)) {
        const catObj = cat as {
          title?: string;
          name?: string;
          description?: string;
          [key: string]: unknown;
        };
        const title = catObj?.title;
        const name = catObj?.name;
        const description = catObj?.description;
        // Prefer title, fallback to name, then description
        const result =
          (typeof title === 'string' ? title.trim() : '') ||
          (typeof name === 'string' ? name.trim() : '') ||
          (typeof description === 'string' ? description.trim() : '');
        return result.length > 0 ? result : null;
      }
      return null;
    };

    // Add categories from the 'category' field (main category)
    if (exp?.category !== null && exp?.category !== undefined) {
      console.log(
        '[ExperienceHeader] Processing category field:',
        exp.category
      );
      // Handle category as array
      if (Array.isArray(exp.category)) {
        exp.category.forEach(cat => {
          const catName = extractCategoryName(cat);
          console.log(
            '[ExperienceHeader] Extracted from category array:',
            catName
          );
          if (catName && !categories.includes(catName)) {
            categories.push(catName);
          }
        });
      }
      // Handle category as object (not array)
      else if (typeof exp.category === 'object' && exp.category !== null) {
        const catName = extractCategoryName(exp.category);
        console.log(
          '[ExperienceHeader] Extracted from category object:',
          catName
        );
        if (catName && !categories.includes(catName)) {
          categories.push(catName);
        }
      }
      // Handle category as string
      else if (typeof exp.category === 'string') {
        const trimmed = exp.category.trim();
        console.log(
          '[ExperienceHeader] Extracted from category string:',
          trimmed
        );
        if (trimmed && !categories.includes(trimmed)) {
          categories.push(trimmed);
        }
      }
    }

    // Add categories from the 'categories' array field (additional categories)
    if (
      exp?.categories &&
      Array.isArray(exp.categories) &&
      exp.categories.length > 0
    ) {
      console.log(
        '[ExperienceHeader] Processing categories array:',
        exp.categories
      );
      exp.categories.forEach((cat, index) => {
        const catName = extractCategoryName(cat);
        console.log(
          `[ExperienceHeader] Extracted from categories[${index}]:`,
          catName,
          'Raw:',
          cat
        );
        if (catName && !categories.includes(catName)) {
          categories.push(catName);
        } else if (catName) {
          console.log(
            `[ExperienceHeader] Skipping duplicate category:`,
            catName
          );
        }
      });
    }

    // Fallback to type if no categories found
    if (categories.length === 0 && typeof exp?.type === 'string') {
      console.log(
        '[ExperienceHeader] No categories found, using type fallback:',
        exp.type
      );
      const trimmedType = exp.type.trim();
      if (trimmedType) {
        categories.push(trimmedType);
      }
    }

    console.log('[ExperienceHeader] Final categories array:', categories);
    return categories;
  };

  const getPriceInfo = () => {
    const exp = experience as {
      price?: number;
      currency?: string;
      currencySymbol?: string;
      targetCurrencyRate?: number;
      useGroupPricing?: boolean;
      groupBooking?: Array<{
        isDisplayPrice?: boolean;
        minHeadCount?: number;
        maxHeadCount?: number;
        adultPrice?: number;
        targetCurrencyRate?: number;
      }>;
      pricing?: {
        pricePerPerson?: number;
        currency?: string;
        groupPricing?: Array<{
          isDisplayPrice?: boolean;
          minHeadCount?: number;
          maxHeadCount?: number;
          adultPrice?: number;
        }>;
      };
    };

    if (type === 'stay') {
      const backendCurrency = (exp?.pricing?.currency ||
        'USD') as BackendCurrency;

      // When a traveler count is provided, reflect the tier that matches that
      // count (group when in range, standard per-person price otherwise).
      if (typeof travelerCount === 'number' && travelerCount > 0) {
        const matchedGroup = exp?.pricing?.groupPricing?.find(
          g =>
            typeof g.minHeadCount === 'number' &&
            typeof g.maxHeadCount === 'number' &&
            travelerCount >= g.minHeadCount &&
            travelerCount <= g.maxHeadCount
        );
        return {
          amountValue:
            matchedGroup?.adultPrice ?? exp?.pricing?.pricePerPerson ?? 0,
          pricePer: '/person/night',
          backendCurrency,
          backendConversionRate: undefined,
        };
      }

      const displayGroup = exp?.pricing?.groupPricing?.find(
        g => g.isDisplayPrice
      );
      return displayGroup
        ? {
            amountValue: displayGroup.adultPrice ?? 0,
            pricePer: `/person/night`,
            backendCurrency,
            backendConversionRate: undefined,
          }
        : {
            amountValue: exp?.pricing?.pricePerPerson ?? 0,
            pricePer: '/person/night',
            backendCurrency,
            backendConversionRate: undefined,
          };
    }

    if (type === 'package') {
      const backendCurrency = (exp?.currency ||
        exp?.currencySymbol ||
        'USD') as BackendCurrency;

      // When a traveler count is provided, reflect the matching group tier
      // (or standard per-person price when outside all group ranges).
      if (typeof travelerCount === 'number' && travelerCount > 0) {
        const matchedGroup = exp?.groupBooking?.find(
          g =>
            typeof g.minHeadCount === 'number' &&
            typeof g.maxHeadCount === 'number' &&
            travelerCount >= g.minHeadCount &&
            travelerCount <= g.maxHeadCount
        );
        const resolvedConversionRate =
          typeof matchedGroup?.targetCurrencyRate === 'number'
            ? matchedGroup.targetCurrencyRate
            : typeof exp?.targetCurrencyRate === 'number'
              ? exp.targetCurrencyRate
              : undefined;
        return {
          amountValue: matchedGroup?.adultPrice ?? exp?.price ?? 0,
          pricePer: 'person',
          backendCurrency,
          backendConversionRate: resolvedConversionRate,
        };
      }

      const displayGroup = exp?.useGroupPricing
        ? exp?.groupBooking?.find(g => g.isDisplayPrice)
        : undefined;
      const resolvedConversionRate =
        typeof displayGroup?.targetCurrencyRate === 'number'
          ? displayGroup.targetCurrencyRate
          : typeof exp?.targetCurrencyRate === 'number'
            ? exp.targetCurrencyRate
            : undefined;
      return displayGroup
        ? {
            amountValue:
              (displayGroup.adultPrice ?? 0) * displayGroup.minHeadCount,
            pricePer: `${displayGroup.minHeadCount}${
              displayGroup.maxHeadCount ? `-${displayGroup.maxHeadCount}` : '+'
            } person${
              (displayGroup.maxHeadCount ?? displayGroup.minHeadCount) > 1
                ? 's'
                : ''
            }`,
            backendCurrency,
            backendConversionRate: resolvedConversionRate,
          }
        : {
            amountValue: exp?.price ?? 0,
            pricePer: '',
            backendCurrency,
            backendConversionRate: resolvedConversionRate,
          };
    }

    // Activity
    const activity = exp as {
      price?: number;
      pricePer?: string;
      currency?: string;
      currencySymbol?: string;
      targetCurrencyRate?: number;
      useGroupPricing?: boolean;
      groupBooking?: Array<{
        isDisplayPrice?: boolean;
        minHeadCount?: number;
        maxHeadCount?: number;
        adultPrice?: number;
        targetCurrencyRate?: number;
      }>;
    };
    const activityBackendCurrency = (activity?.currency ||
      activity?.currencySymbol ||
      'USD') as BackendCurrency;

    // When a traveler count is provided, reflect the matching group tier
    // (or standard per-person price when outside all group ranges).
    if (typeof travelerCount === 'number' && travelerCount > 0) {
      const matchedGroup = activity?.groupBooking?.find(
        g =>
          typeof g.minHeadCount === 'number' &&
          typeof g.maxHeadCount === 'number' &&
          travelerCount >= g.minHeadCount &&
          travelerCount <= g.maxHeadCount
      );
      const resolvedConversionRate =
        typeof matchedGroup?.targetCurrencyRate === 'number'
          ? matchedGroup.targetCurrencyRate
          : typeof activity?.targetCurrencyRate === 'number'
            ? activity.targetCurrencyRate
            : undefined;
      return {
        amountValue: matchedGroup?.adultPrice ?? activity?.price ?? 0,
        pricePer: 'person',
        backendCurrency: activityBackendCurrency,
        backendConversionRate: resolvedConversionRate,
      };
    }

    const displayGroup = activity?.useGroupPricing
      ? activity?.groupBooking?.find(g => g.isDisplayPrice)
      : undefined;
    const resolvedConversionRate =
      typeof displayGroup?.targetCurrencyRate === 'number'
        ? displayGroup.targetCurrencyRate
        : typeof activity?.targetCurrencyRate === 'number'
          ? activity.targetCurrencyRate
          : undefined;
    return displayGroup
      ? {
          amountValue:
            (displayGroup.adultPrice ?? 0) * displayGroup.minHeadCount,
          pricePer: `${displayGroup.minHeadCount}${
            displayGroup.maxHeadCount ? `-${displayGroup.maxHeadCount}` : '+'
          } person${
            (displayGroup.maxHeadCount ?? displayGroup.minHeadCount) > 1
              ? 's'
              : ''
          }`,
          backendCurrency: activityBackendCurrency,
          backendConversionRate: resolvedConversionRate,
        }
      : {
          amountValue: activity?.price ?? 0,
          pricePer: activity?.pricePer || '',
          backendCurrency: activityBackendCurrency,
          backendConversionRate: resolvedConversionRate,
        };
  };

  const getDays = (): number | undefined => {
    const exp = experience as {
      duration?: { days?: number; nights?: number };
      days?: number | { days?: number };
    };
    if (exp?.duration && typeof exp.duration === 'object') {
      return exp.duration.days;
    }
    if (typeof exp?.days === 'number') {
      return exp.days;
    }
    if (exp?.days && typeof exp.days === 'object') {
      return (exp.days as { days?: number }).days;
    }
    return undefined;
  };

  const getNights = (): number | undefined => {
    const exp = experience as {
      duration?: { days?: number; nights?: number };
      nights?: number | { nights?: number };
    };
    if (exp?.duration && typeof exp.duration === 'object') {
      return exp.duration.nights;
    }
    if (typeof exp?.nights === 'number') {
      return exp.nights;
    }
    if (exp?.nights && typeof exp.nights === 'object') {
      return (exp.nights as { nights?: number }).nights;
    }
    return undefined;
  };

  const getAttractionsCount = (): number => {
    const exp = experience as {
      attractionCount?: number;
      attraction_count?: number;
      highlightsCount?: number;
      highlights_count?: number;
      attractionsCount?: number;
      highlights?: unknown[];
      attractions?: unknown[];
      activities?: unknown[];
    };
    // Prefer explicit API fields (detail API often uses attractionCount)
    if (typeof exp?.attractionCount === 'number') return exp.attractionCount;
    if (typeof exp?.attraction_count === 'number') return exp.attraction_count;
    if (typeof exp?.highlightsCount === 'number') return exp.highlightsCount;
    if (typeof exp?.highlights_count === 'number') return exp.highlights_count;
    if (typeof exp?.attractionsCount === 'number') return exp.attractionsCount;
    if (Array.isArray(exp?.highlights)) return exp.highlights.length;
    if (Array.isArray(exp?.attractions)) return exp.attractions.length;
    // Do not fall back to activities.length here — getLocationsCount uses
    // activities (one location per activity), which would make both counts
    // identical when API omits attraction/highlight fields.
    return 0;
  };

  const getLocationsCount = (): number => {
    const exp = experience as {
      locationCount?: number;
      locations?: unknown[];
      mainLocation?: unknown;
      location?: unknown;
      activities?: Array<{ locations?: unknown }>;
    };
    if (typeof exp?.locationCount === 'number' && exp.locationCount >= 0)
      return exp.locationCount;
    if (Array.isArray(exp?.locations)) return exp.locations.length;
    if (Array.isArray(exp?.activities)) {
      const allLocations = exp.activities
        .map(activity => activity.locations)
        .filter(Boolean);
      return allLocations.length;
    }
    // Stay detail: backend does not send locationCount; one place = 1 location
    if (type === 'stay' && (exp?.mainLocation || exp?.location)) return 1;
    return 0;
  };

  const getLocationName = (): string | null => {
    const exp = experience as {
      location?: {
        name?: string;
        city?: string;
        state?: string;
      } | null;
      mainLocation?: {
        name?: string;
        city?: string;
        state?: string;
      } | null;
    };

    // Check mainLocation first (preferred API field)
    if (exp?.mainLocation) {
      const loc = exp.mainLocation;
      if (loc.name) return loc.name;
      if (loc.city) {
        return loc.state ? `${loc.city}, ${loc.state}` : loc.city;
      }
    }

    // Fallback to location field
    if (exp?.location) {
      const loc = exp.location;
      if (loc.name) return loc.name;
      if (loc.city) {
        return loc.state ? `${loc.city}, ${loc.state}` : loc.city;
      }
    }

    return null;
  };

  const formatDaysNights = (days?: number, nights?: number): string | null => {
    if (typeof days === 'number' && typeof nights === 'number') {
      return `${days} ${days === 1 ? 'Day' : 'Days'} & ${nights} ${
        nights === 1 ? 'Night' : 'Nights'
      }`;
    }
    if (typeof days === 'number') {
      return `${days} ${days === 1 ? 'Day' : 'Days'}`;
    }
    if (typeof nights === 'number') {
      return `${nights} ${nights === 1 ? 'Night' : 'Nights'}`;
    }
    return null;
  };

  const title = getTitle();
  const categories = getCategories();
  const priceInfo = getPriceInfo();
  const days = getDays();
  const nights = getNights();
  const attractionsCount = getAttractionsCount();
  const locationsCount = getLocationsCount();
  const locationName = getLocationName();

  return (
    <div className='space-y-4'>
      <div>
        <div className='flex items-start justify-between gap-4'>
          <div className='flex-1'>
            {categories.length > 0 && (
              <div className='flex flex-wrap items-center gap-2 mb-2'>
                {categories.map((category, index) => (
                  <React.Fragment key={index}>
                    <p
                      className='text-gray-600'
                      style={{
                        fontSize: typography.size.xs, // 12px
                        fontFamily: typography.fonts.primary,
                      }}
                    >
                      {category}
                    </p>
                    {index < categories.length - 1 && (
                      <span
                        className='text-gray-400'
                        style={{
                          fontSize: typography.size.xs, // 12px
                        }}
                      >
                        •
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
            <div className='flex items-start justify-between gap-4'>
              <h1
                className='font-bold text-gray-900 flex-1'
                style={{
                  fontSize: typography.styles.h3.fontSize, // 32px
                  fontFamily: typography.fonts.primary,
                  lineHeight: typography.styles.h3.lineHeight,
                  // fontWeight: typography.weight.bold,
                }}
              >
                {title}
              </h1>
              <div className='flex-shrink-0'>
                <ShareButton
                  type={type}
                  slug={slug}
                  useFullUrl={hasBookingIdInUrl || shareCurrentUrl}
                />
              </div>
            </div>
            {/* Show duration string only if we don't have individual days/nights counts to display */}
            {(days !== undefined ||
              nights !== undefined ||
              attractionsCount > 0 ||
              locationsCount > 0) && (
              <div className='flex flex-wrap items-center gap-2 mt-2'>
                {[
                  locationName || null,
                  formatDaysNights(days, nights),
                  attractionsCount > 0
                    ? `${attractionsCount} ${
                        attractionsCount === 1 ? 'Attraction' : 'Attractions'
                      }`
                    : null,
                  locationsCount > 0
                    ? `${locationsCount} ${
                        locationsCount === 1 ? 'Location' : 'Locations'
                      }`
                    : null,
                ]
                  .filter(Boolean)
                  .map((text, index, array) => (
                    <React.Fragment key={index}>
                      <span
                        className='text-gray-600'
                        style={{
                          fontSize: typography.size.xs,
                          fontFamily: typography.fonts.primary,
                        }}
                      >
                        {text}
                      </span>
                      {index < array.length - 1 && (
                        <span className='text-gray-400'>•</span>
                      )}
                    </React.Fragment>
                  ))}
              </div>
            )}
          </div>
        </div>
        {!hasBookingIdInUrl && priceInfo.amountValue > 0 && (
          <div className='flex items-baseline mt-4' style={{ gap: '0px' }}>
            <span
              className='font-semibold text-gray-900'
              style={{
                fontSize: typography.size['2xl'], // 24px
                fontFamily: typography.fonts.primary,
              }}
            >
              <CurrencyDisplay
                amount={priceInfo.amountValue}
                backendCurrency={priceInfo.backendCurrency}
                backendConversionRate={priceInfo.backendConversionRate}
                showDecimals={false}
                fontSize={typography.size['2xl']} // 24px
              />
            </span>
            {priceInfo.pricePer && (
              <span
                className='font-semibold text-gray-900'
                style={{
                  fontSize: typography.size.sm, // 14px
                  fontFamily: typography.fonts.primary,
                  marginLeft: '4px',
                  fontWeight: typography.weight.regular,
                }}
              >
                {priceInfo.pricePer.startsWith('/')
                  ? priceInfo.pricePer
                  : `/${priceInfo.pricePer}`}
              </span>
            )}
            {!priceInfo.pricePer && (
              <span
                className='font-semibold text-gray-900'
                style={{
                  fontSize: typography.size.sm, // 14px
                  fontFamily: typography.fonts.primary,
                  marginLeft: '4px',
                  fontWeight: typography.weight.regular,
                }}
              >
                {type === 'stay' ? '/person/night' : '/person'}
              </span>
            )}
          </div>
        )}
      </div>
      <div className='border-b border' style={{ borderColor: '#F5F5F4' }}></div>
    </div>
  );
};
